import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Typography,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  ShoppingCart as OrdersIcon,
  Assessment as ReportsIcon,
  Category as CategoryIcon,
  Restaurant as MenuIcon,
  Inventory as InventoryIcon,
  People as ManagementIcon,
  Settings as ConfigIcon,
  Support as SupportIcon,
  Store as PosIcon,
  Psychology as AiIcon,
  ExpandMore as ExpandMoreIcon,
  // Specific icons for each menu item
  Home as OverviewIcon,
  Receipt as ClosingIcon,
  BarChart as ChartIcon,
  Add as AddIcon,
  ViewList as ListIcon,
  Tune as TuneIcon,
  Notifications as NotificationsIcon,
  Visibility as ActivityIcon,
  Book as ResourceIcon,
  Headset as LiveSupportIcon,
  Business as VendorIcon,
  People as UserIcon,
  // ✅ FIXED: Unique inventory icons with no duplicates
  Assignment as StockCountIcon,
  DeleteOutline as WastageIcon,
  Build as ProductionIcon,
  // ✅ FIXED: Unique report icons
  Assessment as SalesReportIcon,
  ReceiptLong as ClosingReportIcon,
  Storage as InventoryReportIcon,
  Settings as SettingsIcon,

} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const drawerWidth = 260;

// Separate component for nested menu items to handle useState properly
const NestedMenuItem = ({ item, level, hasRoutePermission, handleNavigation, isActive, theme }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box key={item.title}>
      <ListItemButton
        onClick={() => setExpanded(!expanded)}
        sx={{
          pl: 3 + level * 2,
          mb: 0.5,
          borderRadius: '8px',
          mx: 1,
        }}
      >
        {item.icon && (
          <ListItemIcon sx={{ minWidth: 32 }}>
            {item.icon}
          </ListItemIcon>
        )}
        <ListItemText 
          primary={
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              {item.title}
            </Typography>
          } 
        />
        <ExpandMoreIcon 
          sx={{ 
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }} 
        />
      </ListItemButton>
      <Collapse in={expanded}>
        <List component="div" disablePadding>
          {item.children.map((child) => {
            if (child.permission && !hasRoutePermission(child.permission)) {
              return null;
            }

            const MotionDiv = motion.div;

            return (
              <MotionDiv key={child.title} whileHover={{ x: 4 }} transition={{ duration: 0.2 }}>
                <ListItemButton
                  onClick={() => handleNavigation(child.path)}
                  selected={isActive(child.path)}
                  sx={{
                    pl: 3 + (level + 1) * 2,
                    mb: 0.5,
                    borderRadius: '8px',
                    mx: 1,
                    color: isActive(child.path) ? theme.palette.primary.main : 'inherit',
                  }}
                >
                  {child.icon && (
                    <ListItemIcon
                      sx={{
                        minWidth: 32,
                        color: isActive(child.path) ? theme.palette.primary.main : 'inherit',
                      }}
                    >
                      {child.icon}
                    </ListItemIcon>
                  )}
                  <ListItemText 
                    primary={
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {child.title}
                      </Typography>
                    } 
                  />
                  {child.badge && (
                    <Badge badgeContent={child.badge} color="error" sx={{ mr: 1 }} />
                  )}
                </ListItemButton>
              </MotionDiv>
            );
          }).filter(Boolean)}
        </List>
      </Collapse>
    </Box>
  );
};

const Sidebar = ({ handleDrawerToggle, mobileOpen }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedSections, setExpandedSections] = useState({});
  const [userPermissions, setUserPermissions] = useState([]);
  const [userData, setUserData] = useState(null);

  const MotionDiv = motion.div;

  // Role-based access control helper
  const hasAccess = useCallback((section, userRole) => {
    if (!userRole) return false;
    
    switch (userRole) {
      case 'Admin':
        return true; // Admin has full access
      
      case 'Manager':
        // Manager cannot see Management or Config & Notifications
        return !['management', 'config'].includes(section);
      
      case 'Staff':
        // Staff only sees Operations, Menu & Inventory, and POSlah!
        return ['operations', 'menu', 'pos'].includes(section);
      
      default:
        return false;
    }
  }, []);

  // Check if user has permission for specific route
  const hasRoutePermission = useCallback((permissionName) => {
    const userRole = userData?.role;
    
    // Basic pages accessible to all authenticated users
    if (!permissionName || 
        permissionName === 'home' || 
        permissionName === '/dashboard' || 
        permissionName === '/dashboard/profile' || 
        permissionName === '/dashboard/pos') {
      return true;
    }
    
    // Admin has access to everything
    if (userRole === 'Admin') {
      return true;
    }
    
    // Role-based permissions
    if (userRole === 'Manager') {
      // Manager restrictions
      if (permissionName === '/dashboard/users' || 
          permissionName === '/dashboard/vendor-settings' ||
          permissionName === '/dashboard/pos-config' ||
          permissionName === '/dashboard/notifications' ||
          permissionName === '/dashboard/activity-log') {
        return false;
      }
      return true;
    }
    
    if (userRole === 'Staff') {
      // Staff restrictions - only basic operations and menu management
      const allowedPaths = [
        '/dashboard',
        '/dashboard/orders',
        '/dashboard/closing-sales',
        '/dashboard/sales',
        '/dashboard/category/add',
        '/dashboard/category/list',
        '/dashboard/menu/add',
        '/dashboard/menu/list',
        '/dashboard/inventory/stock',
        '/dashboard/inventory/wastage',
        '/dashboard/inventory/production',
        '/dashboard/pos'
      ];
      return allowedPaths.includes(permissionName);
    }
    
    // Fallback to permission array check
    return userPermissions.some(p => p.url === permissionName);
  }, [userData?.role, userPermissions]);

  // Sidebar navigation structure
  const navigationSections = useMemo(() => [
    {
      id: 'operations',
      title: t('nav.sections.operations'),
      icon: <DashboardIcon />,
      access: ['Admin', 'Manager', 'Staff'],
      items: [
        {
          title: t('nav.overview'),
          path: '/dashboard',
          icon: <OverviewIcon fontSize="small" />,
          permission: 'home'
        },
        {
          title: t('nav.orders'),
          path: '/dashboard/orders',
          icon: <OrdersIcon fontSize="small" />,
          permission: '/dashboard/orders'
        },
        {
          title: t('nav.closingSales'),
          path: '/dashboard/closing-sales',
          icon: <ClosingIcon fontSize="small" />,
          permission: '/dashboard/closing-sales'
        },
        {
          title: t('nav.reports'),
          icon: <ReportsIcon fontSize="small" />,
          children: [
            {
              title: t('nav.salesReport'),
              path: '/dashboard/sales',
              icon: <SalesReportIcon fontSize="small" />,
              permission: '/dashboard/sales'
            },
            {
              title: t('nav.closingReport'),
              path: '/dashboard/reports/closing',
              icon: <ClosingReportIcon fontSize="small" />,
              permission: '/dashboard/reports/closing'
            },
            {
              title: t('nav.inventoryReport'),
              path: '/dashboard/reports/inventory',
              icon: <InventoryReportIcon fontSize="small" />,
              permission: '/dashboard/reports/inventory'
            }
          ]
        }
      ]
    },
    {
      id: 'menu',
      title: t('nav.sections.menuInventory'),
      icon: <CategoryIcon />,
      access: ['Admin', 'Manager', 'Staff'],
      items: [
        {
          title: t('nav.categories'),
          icon: <CategoryIcon fontSize="small" />,
          children: [
            {
              title: t('nav.addCategory'),
              path: '/dashboard/category/add',
              icon: <AddIcon fontSize="small" />,
              permission: '/dashboard/category/add'
            },
            {
              title: t('nav.categoriesList'),
              path: '/dashboard/category/list',
              icon: <ListIcon fontSize="small" />,
              permission: '/dashboard/category/list'
            }
          ]
        },
        {
          title: t('nav.menu'),
          icon: <MenuIcon fontSize="small" />,
          children: [
            {
              title: t('nav.addMenu'),
              path: '/dashboard/menu/add',
              icon: <AddIcon fontSize="small" />,
              permission: '/dashboard/menu/add'
            },
            {
              title: t('nav.menuList'),
              path: '/dashboard/menu/list',
              icon: <ListIcon fontSize="small" />,
              permission: '/dashboard/menu/list'
            }
          ]
        },
        {
          title: t('nav.inventory'),
          icon: <InventoryIcon fontSize="small" />,
          children: [
            {
              title: t('nav.ingredients'),
              path: '/dashboard/inventory/ingredients',
              icon: <SettingsIcon fontSize="small" />,
              permission: '/dashboard/inventory/ingredients'
            },
            // {
            //   title: t('nav.ingredientsPurchase'),
            //   path: '/dashboard/inventory/ingredients/purchases',
            //   icon: <SettingsIcon fontSize="small" />,
            //   permission: '/dashboard/inventory/ingredients/purchases'
            // },
            {
              title: t('nav.stockSetup'), // ✅ ADD THIS
              path: '/dashboard/inventory/setup',
              icon: <SettingsIcon fontSize="small" />,
              permission: '/dashboard/inventory/setup'
            },
            {
              title: t('nav.stockCount'),
              path: '/dashboard/inventory/stock',
              icon: <StockCountIcon fontSize="small" />,
              permission: '/dashboard/inventory/stock'
            },
            {
              title: t('nav.productionLog'),
              path: '/dashboard/inventory/production',
              icon: <ProductionIcon fontSize="small" />,
              permission: '/dashboard/inventory/production'
            },
            {
              title: t('nav.wastageLog'),
              path: '/dashboard/inventory/wastage',
              icon: <WastageIcon fontSize="small" />,
              permission: '/dashboard/inventory/wastage'
            }
          ]
        }
      ]
    },
    {
      id: 'management',
      title: t('nav.sections.management'),
      icon: <ManagementIcon />,
      access: ['Admin'],
      items: [
        {
          title: t('nav.userManagement'),
          path: '/dashboard/users',
          icon: <UserIcon fontSize="small" />,
          permission: '/dashboard/users'
        },
        {
          title: t('nav.vendorSettings'),
          path: '/dashboard/vendor-settings',
          icon: <VendorIcon fontSize="small" />,
          permission: '/dashboard/vendor-settings'
        }
      ]
    },
    {
      id: 'config',
      title: t('nav.sections.configNotifications'),
      icon: <ConfigIcon />,
      access: ['Admin'],
      items: [
        {
          title: t('nav.posConfig'),
          path: '/dashboard/pos-config',
          icon: <TuneIcon fontSize="small" />,
          permission: '/dashboard/pos-config'
        },
        {
          title: t('nav.notificationCenter'),
          path: '/dashboard/notifications',
          icon: <NotificationsIcon fontSize="small" />,
          permission: '/dashboard/notifications',
          badge: 3 // Example badge for unread notifications
        },
        {
          title: t('nav.userActivityLog'),
          path: '/dashboard/activity-log',
          icon: <ActivityIcon fontSize="small" />,
          permission: '/dashboard/activity-log'
        }
      ]
    },
    {
      id: 'support',
      title: t('nav.sections.support'),
      icon: <SupportIcon />,
      access: ['Admin', 'Manager'],
      items: [
        {
          title: t('nav.resources'),
          path: '/dashboard/resources',
          icon: <ResourceIcon fontSize="small" />,
          permission: '/dashboard/resources'
        },
        {
          title: t('nav.liveSupport'),
          path: '/dashboard/support',
          icon: <LiveSupportIcon fontSize="small" />,
          permission: '/dashboard/support'
        }
      ]
    }
  ], [t]);

  // Initialize user data and permissions
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/signin');
      return;
    }

    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      setUserData(tokenData);
      
      if (tokenData.permissions) {
        setUserPermissions(tokenData.permissions);
      } else {
        const storedPermissions = localStorage.getItem('userPermissions');
        if (storedPermissions) {
          try {
            const parsedPermissions = JSON.parse(storedPermissions);
            setUserPermissions(parsedPermissions);
          } catch (error) {
            console.error('Error parsing permissions', error);
            setUserPermissions([]);
          }
        }
      }
    } catch (error) {
      console.error('Error processing token', error);
    }
  }, [navigate]);

  // Set initially expanded sections based on current path
  useEffect(() => {
    const setInitialExpanded = () => {
      const initialExpanded = {};
      
      navigationSections.forEach(section => {
        const hasActiveChild = section.items.some(item => 
          (item.path && location.pathname.startsWith(item.path)) ||
          (item.children && item.children.some(child => 
            child.path && location.pathname.startsWith(child.path)
          ))
        );
        
        if (hasActiveChild) {
          initialExpanded[section.id] = true;
        }
      });
      
      return initialExpanded;
    };
    
    setExpandedSections(setInitialExpanded());
  }, [location.pathname, navigationSections]);

  // Handle section toggle
  const handleSectionToggle = useCallback((sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  }, []);

  // Navigation handler
  const handleNavigation = useCallback((path) => {
    if (path && path !== location.pathname) {
      navigate(path, { replace: false });
      if (isMobile) {
        handleDrawerToggle();
      }
    }
  }, [navigate, location.pathname, isMobile, handleDrawerToggle]);

  // Check if path is active
  const isActive = useCallback((path) => {
    if (path === '/dashboard' && location.pathname === '/dashboard') {
      return true;
    }
    return path !== '/dashboard' && location.pathname.startsWith(path);
  }, [location.pathname]);

  // Render menu items recursively - FIXED: Removed useState from callback
  const renderMenuItems = useCallback((items, level = 0) => {
    return items.map((item) => {
      // Check permission
      if (item.permission && !hasRoutePermission(item.permission)) {
        return null;
      }

      const hasChildren = item.children && item.children.length > 0;
      
      if (!hasChildren) {
        // Simple menu item
        return (
          <MotionDiv key={item.title} whileHover={{ x: 4 }} transition={{ duration: 0.2 }}>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={isActive(item.path)}
              sx={{
                pl: 3 + level * 2,
                mb: 0.5,
                borderRadius: '8px',
                mx: 1,
                color: isActive(item.path) ? theme.palette.primary.main : 'inherit',
              }}
            >
              {item.icon && (
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color: isActive(item.path) ? theme.palette.primary.main : 'inherit',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
              )}
              <ListItemText 
                primary={
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {item.title}
                  </Typography>
                } 
              />
              {item.badge && (
                <Badge badgeContent={item.badge} color="error" sx={{ mr: 1 }} />
              )}
            </ListItemButton>
          </MotionDiv>
        );
      } else {
        // Menu item with children - Use separate component to handle useState
        return (
          <NestedMenuItem
            key={item.title}
            item={item}
            level={level}
            hasRoutePermission={hasRoutePermission}
            handleNavigation={handleNavigation}
            isActive={isActive}
            theme={theme}
          />
        );
      }
    }).filter(Boolean);
  }, [hasRoutePermission, handleNavigation, isActive, theme]);

  // Handle bottom navigation
  const handlePosNavigation = useCallback(() => {
    navigate('/dashboard/pos');
    if (isMobile) {
      handleDrawerToggle();
    }
  }, [navigate, isMobile, handleDrawerToggle]);

  const handleAiNavigation = useCallback(() => {
    navigate('/dashboard/ai-insight');
    if (isMobile) {
      handleDrawerToggle();
    }
  }, [navigate, isMobile, handleDrawerToggle]);

  // Render the drawer content
  const drawer = useMemo(() => (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 1,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          POSlah
        </Typography>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {/* Main Navigation */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', px: 1 }}>
        {navigationSections.map((section) => {
          // Check if user has access to this section
          if (!hasAccess(section.id, userData?.role)) {
            return null;
          }

          return (
            <Accordion
              key={section.id}
              expanded={expandedSections[section.id] || false}
              onChange={() => handleSectionToggle(section.id)}
              elevation={0}
              disableGutters
              sx={{
                mb: 1,
                '&:before': {
                  display: 'none',
                },
                '& .MuiAccordionSummary-root': {
                  minHeight: 48,
                  borderRadius: '12px',
                  px: 2,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    gap: 1.5,
                  },
                }}
              >
                <Box sx={{ color: 'primary.main' }}>
                  {section.icon}
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {section.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List component="div" disablePadding>
                  {renderMenuItems(section.items)}
                </List>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* Bottom Fixed Buttons */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* POSlah! Button */}
        <MotionDiv whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <ListItemButton
            onClick={handlePosNavigation}
            sx={{
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              py: 1.5,
              '&:hover': {
                background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
              <PosIcon />
            </ListItemIcon>
            <ListItemText 
              primary={
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  POSlah!
                </Typography>
              } 
            />
          </ListItemButton>
        </MotionDiv>

        {/* BOTlah! Button - Only for Admin and Manager */}
        {hasAccess('ai', userData?.role) && (
          <MotionDiv whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <ListItemButton
              onClick={handleAiNavigation}
              sx={{
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                color: 'white',
                py: 1.5,
                '&:hover': {
                  background: 'linear-gradient(135deg, #ff5252 0%, #e53935 100%)',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                <AiIcon />
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    BOTlah!
                  </Typography>
                } 
              />
            </ListItemButton>
          </MotionDiv>
        )}
      </Box>
    </Box>
  ), [
    navigationSections, 
    expandedSections, 
    hasAccess, 
    userData?.role, 
    handleSectionToggle, 
    renderMenuItems, 
    handlePosNavigation, 
    handleAiNavigation, 
    theme.palette.divider
  ]);

  return (
    <Box
      component="nav"
      sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            border: 'none',
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;